import React, { useEffect, useMemo, useState } from "react";
import {
    Shield,
    RefreshCw,
    AlertCircle,
    CheckCircle2,
    Filter,
    Eye,
    UserCog,
    LogIn,
    LogOut,
    Trash2,
    FileSignature,
    Download,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, listAuditLogs } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { fmtDateTime } from "@/lib/format";

const ACTION_META = {
    "auth.login.ok": { label: "Login OK", color: "emerald", Icon: LogIn },
    "auth.login.failed": { label: "Login fallido", color: "red", Icon: AlertCircle },
    "auth.logout.ok": { label: "Logout", color: "slate", Icon: LogOut },
    "admin.user.created": { label: "Usuario creado", color: "blue", Icon: UserCog },
    "admin.user.updated": { label: "Usuario actualizado", color: "indigo", Icon: UserCog },
    "admin.user.deleted": { label: "Usuario eliminado", color: "red", Icon: UserCog },
    "tender.deleted": { label: "Licitación eliminada", color: "red", Icon: Trash2 },
    "annex.signed": { label: "Anexo firmado", color: "violet", Icon: FileSignature },
};

const FILTER_OPTIONS = [
    { value: "all", label: "Todos los eventos" },
    { value: "auth", label: "Solo autenticación" },
    { value: "admin", label: "Solo administración" },
    { value: "tender", label: "Solo licitaciones" },
    { value: "annex", label: "Solo firmas" },
];

const colorMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
    slate: "bg-slate-50 text-slate-600 border-slate-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
};

export default function Auditoria() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionFilter, setActionFilter] = useState("all");
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!user?.is_admin) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionFilter]);

    const load = async () => {
        setLoading(true);
        try {
            const data = await listAuditLogs({
                limit: 500,
                action: actionFilter === "all" ? "" : actionFilter,
            });
            setItems(data.items || []);
        } catch (e) {
            toast.error("No se pudieron cargar los eventos de auditoría");
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        if (!s) return items;
        return items.filter(
            (it) =>
                (it.action || "").toLowerCase().includes(s) ||
                (it.user_email || "").toLowerCase().includes(s) ||
                (it.ip || "").toLowerCase().includes(s) ||
                (it.detail || "").toLowerCase().includes(s)
        );
    }, [items, search]);

    const exportCsv = async (days) => {
        try {
            const params = { days };
            if (actionFilter !== "all") params.action = actionFilter;
            const res = await api.get("/admin/audit-logs/export-csv", {
                params,
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `auditoria-${days}d.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Auditoría exportada (${days} días)`);
        } catch {
            toast.error("No se pudo exportar la auditoría");
        }
    };

    const stats = useMemo(() => {
        const total = items.length;
        const failed = items.filter((i) => i.status === "failed").length;
        const last7 = items.filter((i) => {
            const t = i.ts * 1000;
            return t > Date.now() - 7 * 24 * 3600 * 1000;
        }).length;
        const distinctIps = new Set(items.map((i) => i.ip).filter(Boolean)).size;
        return { total, failed, last7, distinctIps };
    }, [items]);

    if (!user?.is_admin) {
        return <Navigate to="/app" replace />;
    }

    return (
        <Layout>
            <div className="space-y-6" data-testid="auditoria-page">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="overline mb-1.5">
                            <Shield size={12} className="inline mr-1" />
                            Auditoría · ISO 27001
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-ink-950">
                            Eventos de seguridad
                        </h1>
                        <p className="text-ink-500 mt-2 text-base font-medium max-w-2xl">
                            Trazabilidad completa de accesos, cambios admin, eliminaciones y firmas.
                            Auditable, exportable y útil para revisiones ISO 27001.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select
                            onValueChange={(v) => exportCsv(parseInt(v, 10))}
                        >
                            <SelectTrigger
                                className="rounded-xl bg-white border-violet-200 text-violet-700 font-bold text-xs w-44 h-9 hover:bg-violet-50"
                                data-testid="audit-export-trigger"
                            >
                                <span className="inline-flex items-center gap-1.5">
                                    <Download size={13} />
                                    Exportar CSV
                                </span>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="30" data-testid="audit-export-30">
                                    Últimos 30 días
                                </SelectItem>
                                <SelectItem value="90" data-testid="audit-export-90">
                                    Últimos 90 días (recomendado)
                                </SelectItem>
                                <SelectItem value="180" data-testid="audit-export-180">
                                    Últimos 180 días
                                </SelectItem>
                                <SelectItem value="365" data-testid="audit-export-365">
                                    Último año
                                </SelectItem>
                            </SelectContent>
                        </Select>
                        <Button
                            variant="outline"
                            onClick={load}
                            disabled={loading}
                            className="rounded-xl"
                            data-testid="audit-refresh"
                        >
                            <RefreshCw
                                size={14}
                                className={`mr-1.5 ${loading ? "animate-spin" : ""}`}
                            />
                            Refrescar
                        </Button>
                    </div>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white border border-slate-200 rounded-2xl p-5">
                        <div className="overline mb-2">
                            <Eye size={12} className="inline mr-1" /> Eventos totales
                        </div>
                        <div className="font-display text-3xl font-black text-ink-950">
                            {stats.total.toLocaleString("es-ES")}
                        </div>
                    </div>
                    <div className="bg-white border border-red-200 rounded-2xl p-5">
                        <div className="overline mb-2 text-red-700">
                            <AlertCircle size={12} className="inline mr-1" /> Fallidos
                        </div>
                        <div className="font-display text-3xl font-black text-red-700">
                            {stats.failed.toLocaleString("es-ES")}
                        </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-2xl p-5">
                        <div className="overline mb-2">
                            <CheckCircle2 size={12} className="inline mr-1" /> Últimos 7 días
                        </div>
                        <div className="font-display text-3xl font-black text-ink-950">
                            {stats.last7.toLocaleString("es-ES")}
                        </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-2xl p-5">
                        <div className="overline mb-2">IPs únicas</div>
                        <div className="font-display text-3xl font-black text-ink-950">
                            {stats.distinctIps.toLocaleString("es-ES")}
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Filter size={14} className="text-ink-400" />
                        <Select value={actionFilter} onValueChange={setActionFilter}>
                            <SelectTrigger className="rounded-xl w-56" data-testid="audit-filter-action">
                                <SelectValue placeholder="Filtrar por tipo" />
                            </SelectTrigger>
                            <SelectContent>
                                {FILTER_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Input
                        placeholder="Buscar por email, IP, detalle…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="rounded-xl w-64"
                        data-testid="audit-search"
                    />
                    <div className="text-xs text-ink-400 font-medium">
                        {filtered.length} de {items.length} eventos
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
                        <table className="w-full data-table text-sm">
                            <thead className="sticky top-0 bg-slate-50/95 backdrop-blur z-10">
                                <tr>
                                    <th className="w-44">Cuándo</th>
                                    <th className="w-48">Acción</th>
                                    <th>Usuario</th>
                                    <th className="w-36">IP</th>
                                    <th>Detalle</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="text-center py-12 text-ink-400">
                                            Cargando eventos…
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center py-12 text-ink-400">
                                            No hay eventos para mostrar
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((it, i) => {
                                        const meta = ACTION_META[it.action] || {
                                            label: it.action,
                                            color: "slate",
                                            Icon: Shield,
                                        };
                                        const Icon = meta.Icon;
                                        const badge = colorMap[meta.color] || colorMap.slate;
                                        return (
                                            <tr
                                                key={`audit-${it.ts}-${i}`}
                                                data-testid={`audit-row-${i}`}
                                            >
                                                <td className="text-xs text-ink-500 font-mono whitespace-nowrap">
                                                    {fmtDateTime(it.ts_iso)}
                                                </td>
                                                <td className="whitespace-nowrap">
                                                    <span
                                                        className={`inline-flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-xl border ${badge}`}
                                                    >
                                                        <Icon size={11} />
                                                        {meta.label}
                                                    </span>
                                                </td>
                                                <td className="text-xs text-ink-700 font-medium">
                                                    {it.user_email || (
                                                        <span className="text-ink-400 italic">
                                                            anónimo
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-xs text-ink-500 font-mono">
                                                    {it.ip || "—"}
                                                </td>
                                                <td className="text-xs text-ink-500 leading-snug">
                                                    {it.detail || (
                                                        <span className="text-ink-400">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
